import { useState, useEffect, useRef, useCallback } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { fetchEcommerceProdutos, fetchSugestaoCompra } from '../../services/api';
import ProductImage from '../ProductImage/ProductImage';
import ProductHoverCard from '../ProductHoverCard/ProductHoverCard';

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

// Executa `fn` sobre `items` com no máximo `limit` em paralelo (evita martelar o backend).
async function mapLimit(items, limit, fn) {
  const ret = new Array(items.length);
  let i = 0;
  const workers = Array.from({ length: Math.min(limit, items.length || 1) }, async () => {
    while (i < items.length) {
      const idx = i++;
      ret[idx] = await fn(items[idx], idx);
    }
  });
  await Promise.all(workers);
  return ret;
}

const brl = (v) => (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/**
 * Compras Automáticas — peças que venderam mais que `threshold` TANTO no mês passado
 * QUANTO no mês atual, já com a quantidade sugerida. Hover mostra o mesmo card do
 * catálogo (ProductHoverCard). O comprador marca o que comprar e gera PDF/CSV ou
 * joga na Lista de Compras. A quantidade vem da MESMA fonte do card (fetchSugestaoCompra).
 */
export default function ComprasAutomaticasModal({
  isOpen, onClose, onAbrirLista, onAplicarLote, adicionarItem,
  fetchProdutos = fetchEcommerceProdutos, // fonte da lista (default: e-commerce)
  lojaSugestao = 'ecommerce',             // loja p/ a sugestão de compra (ecommerce|centro|caxias)
  contexto = 'E-commerce',                // rótulo no título/PDF
}) {
  const [mediaMin, setMediaMin] = useState(1);
  const [mediaMax, setMediaMax] = useState(''); // vazio = sem limite superior
  const [copiado, setCopiado] = useState(false);
  const [filtroFornecedor, setFiltroFornecedor] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [produtos, setProdutos] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [progresso, setProgresso] = useState({ done: 0, total: 0 });
  const [erro, setErro] = useState(null);
  const [qtdEdit, setQtdEdit] = useState({});   // overrides de quantidade por código
  const [selMap, setSelMap] = useState({});     // seleção explícita por código (comprar/não comprar)
  const [hoverProd, setHoverProd] = useState(null);
  const [ordem, setOrdem] = useState({ col: null, dir: 'desc' }); // ordenação por clique no cabeçalho
  const [hoverPos, setHoverPos] = useState({ top: 0, left: 0 });

  const baseRef = useRef([]);        // lista completa (vendas desc), buscada 1x por abertura
  const sugCacheRef = useRef({});    // código -> sugestão já calculada
  const reqIdRef = useRef(0);
  const debounceRef = useRef(null);

  // Mês anterior (calendário) — usado pra exigir venda forte também no mês passado
  const now = new Date();
  let prevY = now.getFullYear();
  let prevM = now.getMonth() + 1 - 1; // getMonth() 0-11 → mês atual 1-12, menos 1 = anterior
  if (prevM === 0) { prevM = 12; prevY -= 1; }
  const prevLabel = MESES[prevM - 1];
  const vendasMesPassado = useCallback((p) => {
    const arr = p.vendasMensais || [];
    const hit = arr.find(v => v.ano === prevY && v.mes === prevLabel);
    return hit ? (hit.quantidade || 0) : 0;
  }, [prevY, prevLabel]);

  // Média diária ÚNICA por peça: vendas dos 2 meses ÷ dias corridos dos 2 meses
  // (mês passado inteiro + dias já decorridos do atual). É nela que a faixa é aplicada.
  const diasMesPassado = new Date(prevY, prevM, 0).getDate();
  const diasMesAtual = Math.max(1, now.getDate());
  const mediaDia = useCallback(
    (p) => (vendasMesPassado(p) + (p.vendasMesAtual || 0)) / (diasMesPassado + diasMesAtual),
    [vendasMesPassado, diasMesPassado, diasMesAtual]
  );
  const fmtMedia = (v) => (Math.round(v * 10) / 10).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  const carregar = useCallback(async (min, max) => {
    const reqId = ++reqIdRef.current;
    setCarregando(true);
    setErro(null);
    try {
      if (!baseRef.current.length) {
        const resp = await fetchProdutos({ page: 1, limit: 500, sort: 'vendasMesAtual', order: 'desc' });
        baseRef.current = (resp && resp.data) || [];
      }
      // Critério: a média diária da peça dentro da faixa [min, max]
      const hi = (max === '' || max === null || isNaN(max)) ? Infinity : Number(max);
      const candidatos = baseRef.current.filter(p => {
        const m = mediaDia(p);
        return m >= min && m <= hi;
      });
      setProgresso({ done: 0, total: candidatos.length });
      let done = 0;
      const comSug = await mapLimit(candidatos, 6, async (p) => {
        let sug = sugCacheRef.current[p.codigo];
        if (sug === undefined) {
          try {
            const d = await fetchSugestaoCompra(p.codigo, {
              loja: lojaSugestao, estoque: p.estoque, precoCusto: p.precoCusto, fornecedor: p.fornecedor
            });
            if (d && d.success && typeof d.sugestao === 'number') sug = d.sugestao;
            else if (d && d.cache_miss) sug = null;
            else sug = 0;
          } catch (_) { sug = null; }
          sugCacheRef.current[p.codigo] = sug;
        }
        if (reqId === reqIdRef.current) setProgresso({ done: ++done, total: candidatos.length });
        return { ...p, sugestao: sug, vendasMesPassado: vendasMesPassado(p), mediaDia: mediaDia(p) };
      });
      if (reqId !== reqIdRef.current) return;
      comSug.sort((a, b) => (b.sugestao || 0) - (a.sugestao || 0) || (b.vendasMesAtual || 0) - (a.vendasMesAtual || 0));
      setProdutos(comSug);
    } catch (_) {
      if (reqId === reqIdRef.current) setErro('Não consegui carregar agora. Clique em "Recarregar".');
    } finally {
      if (reqId === reqIdRef.current) setCarregando(false);
    }
  }, [vendasMesPassado, mediaDia]);

  useEffect(() => {
    if (isOpen) {
      setQtdEdit({});
      setSelMap({});
      setHoverProd(null);
      setFiltroFornecedor('');
      setFiltroCategoria('');
      setOrdem({ col: null, dir: 'desc' });
      carregar(mediaMin, mediaMax);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const agendarCarga = (min, max) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => carregar(min, max), 350);
  };
  const onChangeMediaMin = (val) => {
    const n = Math.max(0, parseFloat(String(val).replace(',', '.')) || 0);
    setMediaMin(n);
    agendarCarga(n, mediaMax);
  };
  const onChangeMediaMax = (val) => {
    const v = val === '' ? '' : Math.max(0, parseFloat(String(val).replace(',', '.')) || 0);
    setMediaMax(v);
    agendarCarga(mediaMin, v);
  };

  if (!isOpen) return null;

  const getQtd = (p) => (qtdEdit[p.codigo] !== undefined ? qtdEdit[p.codigo] : (p.sugestao || 0));
  const setQtd = (codigo, v) => setQtdEdit(prev => ({ ...prev, [codigo]: Math.max(0, parseInt(v, 10) || 0) }));
  const isSel = (p) => (selMap[p.codigo] === true); // abre com NADA marcado — o comprador escolhe
  const toggleSel = (p) => setSelMap(prev => ({ ...prev, [p.codigo]: !isSel(p) }));

  // Opções de filtro derivadas das peças carregadas (só aparece o que existe na lista)
  const fornecedores = Array.from(new Set(produtos.map(p => p.fornecedor).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  const categorias = Array.from(new Set(produtos.map(p => p.categoria).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'pt-BR'));

  // Filtros de fornecedor/categoria = filtro de VISÃO: tudo (tabela, totais, PDF/CSV/Lista,
  // códigos) passa a operar só sobre o que está visível. Sem filtro = lista inteira.
  const produtosFiltradosBase = produtos.filter(p =>
    (!filtroFornecedor || p.fornecedor === filtroFornecedor) &&
    (!filtroCategoria || p.categoria === filtroCategoria)
  );

  // Ordenação por clique no cabeçalho: 1º clique desc, 2º asc. Sem clique = ordem padrão (sugestão desc).
  const valorColuna = (p, col) => {
    switch (col) {
      case 'nome': return String(p.nome || '');
      case 'vdAnt': return Number(p.vendasMesPassado || 0);
      case 'vdAtual': return Number(p.vendasMesAtual || 0);
      case 'media': return Number(p.mediaDia || 0);
      case 'estoque': return Number(p.estoque || 0);
      case 'comprar': return Number(getQtd(p) || 0);
      case 'custo': return Number(getQtd(p) || 0) * Number(p.precoCusto || 0);
      default: return 0;
    }
  };
  const produtosFiltrados = ordem.col
    ? [...produtosFiltradosBase].sort((a, b) => {
        const va = valorColuna(a, ordem.col), vb = valorColuna(b, ordem.col);
        const cmp = typeof va === 'string' ? va.localeCompare(vb, 'pt-BR') : va - vb;
        return ordem.dir === 'asc' ? cmp : -cmp;
      })
    : produtosFiltradosBase;
  const clicaOrdem = (col) => setOrdem(prev =>
    prev.col === col ? { col, dir: prev.dir === 'desc' ? 'asc' : 'desc' } : { col, dir: 'desc' }
  );
  const seta = (col) => ordem.col === col ? (ordem.dir === 'desc' ? ' ▼' : ' ▲') : '';

  const selecionaveis = produtosFiltrados.filter(p => p.sugestao > 0);
  const todosMarcados = selecionaveis.length > 0 && selecionaveis.every(p => isSel(p));
  const toggleTodos = () => {
    const novo = !todosMarcados;
    setSelMap(prev => {
      const m = { ...prev };
      selecionaveis.forEach(p => { m[p.codigo] = novo; });
      return m;
    });
  };

  const comprar = produtosFiltrados.filter(p => isSel(p) && getQtd(p) > 0);
  const totalUn = comprar.reduce((s, p) => s + Number(getQtd(p) || 0), 0);
  const totalRS = comprar.reduce((s, p) => s + Number(getQtd(p) || 0) * Number(p.precoCusto || 0), 0);
  const pendentes = produtosFiltrados.filter(p => p.sugestao === null).length;

  // Códigos das peças MARCADAS (pra jogar no filtro de Lote do catálogo)
  const marcadas = produtosFiltrados.filter(p => isSel(p));
  const codigosMarcados = marcadas.map(p => p.codigo);

  const copiarCodigos = async () => {
    if (!codigosMarcados.length) return;
    const txt = codigosMarcados.join('\n'); // um por linha = formato do campo "Busca em Lote"
    try {
      await navigator.clipboard.writeText(txt);
    } catch (_) {
      const ta = document.createElement('textarea');
      ta.value = txt; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch (e) { }
      ta.remove();
    }
    setCopiado(true);
    setTimeout(() => setCopiado(false), 1800);
  };

  const filtrarNoCatalogo = () => {
    if (!codigosMarcados.length || !onAplicarLote) return;
    onAplicarLote(codigosMarcados); // aplica no filtro de Lote e fecha o modal
  };

  const adicionarTodos = () => {
    if (!adicionarItem) return;
    comprar.forEach(p => adicionarItem(p, getQtd(p)));
    onClose();
    if (onAbrirLista) onAbrirLista();
  };

  const exportarCSV = () => {
    const head = ['Codigo', 'Produto', 'Fornecedor', 'Vd_mes_anterior', 'Vd_mes_atual', 'Media_dia', 'Estoque', 'Comprar_qtd', 'Custo_unit', 'Custo_total'];
    const linhas = comprar.map(p => {
      const q = getQtd(p);
      const cu = Number(p.precoCusto || 0);
      return [p.codigo, `"${String(p.nome || '').replace(/"/g, '""')}"`, `"${String(p.fornecedor || '').replace(/"/g, '""')}"`,
        p.vendasMesPassado || 0, p.vendasMesAtual || 0, (p.mediaDia || 0).toFixed(1), p.estoque || 0, q, cu.toFixed(2), (q * cu).toFixed(2)].join(',');
    });
    const csv = [head.join(','), ...linhas].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'compras-automaticas-ecommerce.csv';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const gerarPDF = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const dataStr = now.toLocaleDateString('pt-BR');
    doc.setFontSize(16); doc.setTextColor(40);
    doc.text(`Compras Automáticas — ${contexto}`, 40, 40);
    doc.setFontSize(10); doc.setTextColor(120);
    doc.text(`Gerado em ${dataStr}  ·  Média diária entre ${mediaMin} e ${mediaMax === '' ? '∞' : mediaMax} un/dia  ·  ${comprar.length} peças`, 40, 58);
    const body = comprar.map(p => {
      const q = getQtd(p);
      const cu = Number(p.precoCusto || 0);
      return [p.codigo, p.nome || '', String(p.vendasMesPassado || 0), String(p.vendasMesAtual || 0), (p.mediaDia || 0).toFixed(1),
        String(p.estoque || 0), String(q), brl(cu), brl(q * cu)];
    });
    autoTable(doc, {
      startY: 74,
      head: [['Código', 'Produto', 'Vd ant.', 'Vd atual', 'Média/dia', 'Estoque', 'Comprar', 'Custo un.', 'Custo total']],
      body,
      styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
      headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: 'bold' },
      columnStyles: { 1: { cellWidth: 140 }, 6: { fontStyle: 'bold' }, 7: { halign: 'right' }, 8: { halign: 'right' } },
      foot: [['', '', '', '', '', '', `${totalUn} un`, 'Total:', brl(totalRS)]],
      footStyles: { fillColor: [241, 245, 249], textColor: 20, fontStyle: 'bold' },
    });
    const slug = String(contexto).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    doc.save(`compras-automaticas-${slug || 'lista'}.pdf`);
  };

  const onRowHover = (p, e) => {
    setHoverProd(p);
    const W = 340, Hv = window.innerHeight * 0.82;
    // Fixa o card na CALHA à esquerda do modal — nunca cobre os números/quantidade (que ficam à direita).
    const modalW = Math.min(1000, window.innerWidth * 0.95);
    const gutter = (window.innerWidth - modalW) / 2;
    const left = gutter >= W + 18 ? (gutter - W - 14) : 12; // sem calha → cola na borda esq. (cobre só as colunas da esquerda)
    let top = (e.clientY || 0) - 60;
    if (top + Hv > window.innerHeight - 12) top = Math.max(12, window.innerHeight - Hv - 12);
    if (top < 12) top = 12;
    setHoverPos({ top, left });
  };

  const th = { padding: '9px 10px', fontSize: '0.7rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.3px', textAlign: 'left', position: 'sticky', top: 0, background: '#f8fafc', borderBottom: '1px solid #e2e8f0', zIndex: 1 };
  const td = { padding: '7px 10px', fontSize: '0.85rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' };
  const selStyle = { padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.82rem', color: '#1e293b', background: '#fff', maxWidth: 240, cursor: 'pointer' };

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1050 }} onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: 'min(1000px, 95vw)', maxHeight: '90vh', background: '#fff', borderRadius: 16,
          boxShadow: '0 24px 64px rgba(15,23,42,0.32)', zIndex: 1051, display: 'flex', flexDirection: 'column', overflow: 'hidden'
        }}
      >
        {/* Cabeçalho */}
        <div style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)', color: '#fff', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontSize: '1.6rem', lineHeight: 1 }}>⚡</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: '1.12rem' }}>Compras Automáticas · {contexto}</div>
            <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>Média de venda diária (últimos 2 meses) dentro da faixa escolhida — passe o mouse pra ver o histórico, marque o que comprar e gere o PDF</div>
          </div>
          <button onClick={onClose} title="Fechar" style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: 8, fontSize: '1.1rem', cursor: 'pointer' }}>✕</button>
        </div>

        {/* Barra de controle + resumo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', padding: '12px 20px', borderBottom: '1px solid #eef2f7', background: '#fcfcfd' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.84rem', color: '#475569', fontWeight: 600 }}>
            Média diária entre
            <input type="number" min={0} step={0.1} value={mediaMin} onChange={(e) => onChangeMediaMin(e.target.value)}
              style={{ width: 72, padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 8, fontWeight: 700, textAlign: 'center' }} />
            e
            <input type="number" min={0} step={0.1} value={mediaMax} placeholder="∞" onChange={(e) => onChangeMediaMax(e.target.value)}
              title="Deixe vazio para não limitar"
              style={{ width: 72, padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 8, fontWeight: 700, textAlign: 'center' }} />
            un / dia
          </label>
          <div style={{ flex: 1 }} />
          {!carregando && !erro && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <Chip cor="#6d28d9" bg="#f5f3ff" label={`${comprar.length} marcadas`} />
              <Chip cor="#1d4ed8" bg="#eff6ff" label={`${totalUn.toLocaleString('pt-BR')} un`} />
              <Chip cor="#15803d" bg="#f0fdf4" label={`${brl(totalRS)} investimento`} />
            </div>
          )}
        </div>

        {/* Filtros: fornecedor + categoria */}
        {!carregando && !erro && produtos.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 20px', borderBottom: '1px solid #eef2f7', background: '#fff' }}>
            <span style={{ fontSize: '0.78rem', color: '#94a3b8', fontWeight: 700 }}>Filtrar:</span>
            <select value={filtroFornecedor} onChange={(e) => setFiltroFornecedor(e.target.value)} style={selStyle} title="Fornecedor">
              <option value="">Todos os fornecedores ({fornecedores.length})</option>
              {fornecedores.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
            <select value={filtroCategoria} onChange={(e) => setFiltroCategoria(e.target.value)} style={selStyle} title="Categoria">
              <option value="">Todas as categorias ({categorias.length})</option>
              {categorias.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            {(filtroFornecedor || filtroCategoria) && (
              <button onClick={() => { setFiltroFornecedor(''); setFiltroCategoria(''); }} style={{ ...botaoSec, padding: '6px 12px', fontSize: '0.78rem' }}>✕ Limpar</button>
            )}
            <span style={{ fontSize: '0.76rem', color: '#94a3b8', marginLeft: 'auto' }}>{produtosFiltrados.length} de {produtos.length} peças</span>
          </div>
        )}

        {/* Corpo */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {carregando ? (
            <div style={{ padding: '48px 20px', textAlign: 'center', color: '#64748b' }}>
              <div style={{ fontSize: '1.8rem', marginBottom: 10 }}>⏳</div>
              <div style={{ fontWeight: 600 }}>Calculando sugestões…</div>
              {progresso.total > 0 && (
                <div style={{ marginTop: 12, maxWidth: 320, margin: '12px auto 0' }}>
                  <div style={{ fontSize: '0.8rem', marginBottom: 6 }}>{progresso.done} de {progresso.total}</div>
                  <div style={{ height: 8, background: '#e2e8f0', borderRadius: 999, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${progresso.total ? (progresso.done / progresso.total) * 100 : 0}%`, background: 'linear-gradient(90deg,#7c3aed,#4f46e5)', transition: 'width .2s' }} />
                  </div>
                </div>
              )}
            </div>
          ) : erro ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: '#b91c1c' }}>
              <div style={{ marginBottom: 12 }}>{erro}</div>
              <button onClick={() => carregar(mediaMin, mediaMax)} style={botaoSec}>Recarregar</button>
            </div>
          ) : produtos.length === 0 ? (
            <div style={{ padding: '48px 20px', textAlign: 'center', color: '#64748b' }}>
              Nenhuma peça com média diária entre {mediaMin} e {mediaMax === '' ? '∞' : mediaMax} un/dia. Tente ampliar a faixa.
            </div>
          ) : produtosFiltrados.length === 0 ? (
            <div style={{ padding: '48px 20px', textAlign: 'center', color: '#64748b' }}>
              Nenhuma peça com esse fornecedor/categoria. <button onClick={() => { setFiltroFornecedor(''); setFiltroCategoria(''); }} style={{ ...botaoSec, padding: '4px 10px', fontSize: '0.8rem', marginLeft: 6 }}>Limpar filtros</button>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...th, width: 40, textAlign: 'center' }}>
                    <input type="checkbox" checked={todosMarcados} onChange={toggleTodos} title="Marcar/desmarcar todos" style={{ cursor: 'pointer' }} />
                  </th>
                  <th style={{ ...th, width: 48 }}></th>
                  <th style={{ ...th, cursor: 'pointer', userSelect: 'none' }} onClick={() => clicaOrdem('nome')} title="Ordenar por nome">Produto{seta('nome')}</th>
                  <th style={{ ...th, textAlign: 'center', width: 72, cursor: 'pointer', userSelect: 'none' }} onClick={() => clicaOrdem('vdAnt')} title="Ordenar">Vd ant.{seta('vdAnt')}</th>
                  <th style={{ ...th, textAlign: 'center', width: 72, cursor: 'pointer', userSelect: 'none' }} onClick={() => clicaOrdem('vdAtual')} title="Ordenar">Vd atual{seta('vdAtual')}</th>
                  <th style={{ ...th, textAlign: 'center', width: 86, cursor: 'pointer', userSelect: 'none' }} onClick={() => clicaOrdem('media')} title="Ordenar">Média<br />(un/dia){seta('media')}</th>
                  <th style={{ ...th, textAlign: 'center', width: 70, cursor: 'pointer', userSelect: 'none' }} onClick={() => clicaOrdem('estoque')} title="Ordenar">Estoque{seta('estoque')}</th>
                  <th style={{ ...th, textAlign: 'center', width: 96, cursor: 'pointer', userSelect: 'none' }} onClick={() => clicaOrdem('comprar')} title="Ordenar">Comprar{seta('comprar')}</th>
                  <th style={{ ...th, textAlign: 'right', width: 110, cursor: 'pointer', userSelect: 'none' }} onClick={() => clicaOrdem('custo')} title="Ordenar">Custo est.{seta('custo')}</th>
                </tr>
              </thead>
              <tbody>
                {produtosFiltrados.map(p => {
                  const q = getQtd(p);
                  const sel = isSel(p);
                  const semSug = p.sugestao === null;
                  const naoComprar = p.sugestao === 0;
                  return (
                    <tr
                      key={p.codigo}
                      onMouseLeave={() => setHoverProd(null)}
                      style={{ background: sel ? '#f5f3ff' : '#fff', cursor: 'default' }}
                    >
                      <td style={{ ...td, textAlign: 'center' }}>
                        <input type="checkbox" checked={sel} disabled={semSug} onChange={() => toggleSel(p)} title={sel ? 'Vai comprar' : 'Não comprar'} style={{ cursor: semSug ? 'default' : 'pointer', width: 16, height: 16 }} />
                      </td>
                      {/* Foto + nome disparam o card de histórico (mouse nos VALORES não dispara, pra não cobrir os números) */}
                      <td style={{ ...td, cursor: 'help' }} onMouseEnter={(e) => onRowHover(p, e)} onMouseMove={(e) => onRowHover(p, e)}>
                        <div style={{ width: 40, height: 40, borderRadius: 8, overflow: 'hidden', background: '#f1f5f9', flexShrink: 0 }}>
                          <ProductImage src={p.imagemURL} alt={p.nome} />
                        </div>
                      </td>
                      <td style={{ ...td, cursor: 'help' }} onMouseEnter={(e) => onRowHover(p, e)} onMouseMove={(e) => onRowHover(p, e)}>
                        <div style={{ fontWeight: 600, lineHeight: 1.25, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title="Passe o mouse aqui pra ver o histórico">{p.nome}</div>
                        <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>#{p.codigo}{p.fornecedor ? ` · ${p.fornecedor}` : ''}</div>
                      </td>
                      <td style={{ ...td, textAlign: 'center' }}>
                        <span style={{ fontWeight: 700, color: '#475569', fontSize: '0.82rem' }}>{p.vendasMesPassado || 0}</span>
                      </td>
                      <td style={{ ...td, textAlign: 'center' }}>
                        <span style={{ fontWeight: 700, color: '#475569', fontSize: '0.82rem' }}>{p.vendasMesAtual || 0}</span>
                      </td>
                      <td style={{ ...td, textAlign: 'center' }}>
                        <span title={`(${p.vendasMesPassado || 0} + ${p.vendasMesAtual || 0} un) ÷ ${diasMesPassado + diasMesAtual} dias`} style={{ fontWeight: 700, color: '#0f766e', background: '#ccfbf1', padding: '2px 8px', borderRadius: 999, fontSize: '0.8rem' }}>{fmtMedia(p.mediaDia || 0)}</span>
                      </td>
                      <td style={{ ...td, textAlign: 'center', color: (p.estoque || 0) <= 0 ? '#dc2626' : '#475569', fontWeight: 600 }}>{p.estoque || 0}</td>
                      <td style={{ ...td, textAlign: 'center' }}>
                        {semSug ? (
                          <span style={{ color: '#94a3b8', fontSize: '0.78rem' }} title="Backend ainda calculando — recarregue em instantes">calculando…</span>
                        ) : (
                          <input type="number" min={0} value={q} onChange={(e) => setQtd(p.codigo, e.target.value)}
                            title={naoComprar ? 'Sugestão: não comprar (estoque cobre a demanda)' : `Sugestão do sistema: ${p.sugestao} un`}
                            style={{ width: 76, padding: '5px 8px', textAlign: 'center', fontWeight: 700, borderRadius: 8, border: `1px solid ${q > 0 ? '#a5b4fc' : '#e2e8f0'}`, background: q > 0 ? '#eef2ff' : '#fff', color: q > 0 ? '#3730a3' : '#94a3b8' }} />
                        )}
                      </td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 600, color: '#334155' }}>{sel && q > 0 ? brl(q * Number(p.precoCusto || 0)) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Rodapé */}
        <div style={{ borderTop: '1px solid #eef2f7', padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 10, background: '#fcfcfd', flexWrap: 'wrap' }}>
          <div style={{ fontSize: '0.78rem', color: '#94a3b8', flex: 1, minWidth: 180 }}>
            {pendentes > 0 ? `${pendentes} item(ns) ainda calculando no servidor — recarregue em instantes.` : 'Marque as peças que vai comprar; o PDF/CSV/Lista sai só com as marcadas.'}
          </div>
          <button onClick={copiarCodigos} disabled={!marcadas.length} style={{ ...botaoSec, opacity: marcadas.length ? 1 : 0.5, color: copiado ? '#15803d' : '#475569', borderColor: copiado ? '#86efac' : '#cbd5e1' }} title="Copia os códigos das marcadas (um por linha) pra colar no campo Busca em Lote">
            {copiado ? '✓ Copiado!' : `📋 Copiar códigos (${marcadas.length})`}
          </button>
          {onAplicarLote && (
            <button onClick={filtrarNoCatalogo} disabled={!marcadas.length} style={{ ...botaoSec, opacity: marcadas.length ? 1 : 0.5 }} title="Joga os códigos das marcadas direto no filtro de Lote do catálogo">
              🔎 Filtrar no catálogo
            </button>
          )}
          <button onClick={gerarPDF} disabled={!comprar.length} style={{ ...botaoSec, opacity: comprar.length ? 1 : 0.5 }}>📑 Gerar PDF</button>
          <button onClick={exportarCSV} disabled={!comprar.length} style={{ ...botaoSec, opacity: comprar.length ? 1 : 0.5 }}>📄 CSV</button>
          <button onClick={adicionarTodos} disabled={!comprar.length} style={{ ...botaoPri, opacity: comprar.length ? 1 : 0.5 }}>
            🛒 Adicionar {comprar.length} à Lista
          </button>
        </div>
      </div>

      {/* Card de hover — mesmo do catálogo */}
      {hoverProd && <ProductHoverCard produto={hoverProd} style={{ top: hoverPos.top, left: hoverPos.left, maxHeight: '82vh', zIndex: 1000000 }} />}
    </>
  );
}

function Chip({ label, cor, bg }) {
  return <span style={{ background: bg, color: cor, padding: '4px 10px', borderRadius: 999, fontSize: '0.78rem', fontWeight: 700, whiteSpace: 'nowrap' }}>{label}</span>;
}

const botaoSec = { background: '#fff', border: '1px solid #cbd5e1', color: '#475569', borderRadius: 10, padding: '9px 14px', fontWeight: 700, fontSize: '0.84rem', cursor: 'pointer' };
const botaoPri = { background: 'linear-gradient(135deg, #16a34a 0%, #0f7a34 100%)', border: 'none', color: '#fff', borderRadius: 10, padding: '9px 16px', fontWeight: 700, fontSize: '0.86rem', cursor: 'pointer', boxShadow: '0 4px 14px rgba(22,163,74,0.35)' };
